import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';
import { SEO, articleLd } from '../components/SEO';

const API = `${window.location.origin}/api`;

export const BlogPost = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${postId}`);
        setPost(response.data);
      } catch (error) {
        // Sample post fallback
        setPost({
          post_id: postId,
          title: 'The Art of Luxury Event Design: 2024 Trends',
          excerpt: 'Discover the latest trends shaping luxury event design this year.',
          content: `
            <p>The world of luxury event design is constantly evolving, with new trends emerging that push the boundaries of creativity and sophistication. As we move through 2024, several key themes are shaping how we approach high-end events.</p>
            
            <h2>Sustainable Elegance</h2>
            <p>Sustainability is no longer an afterthought but a core principle in luxury event design. From locally sourced florals to reusable installations, the most discerning clients now expect their events to minimize environmental impact without compromising on beauty.</p>
            
            <h2>Immersive Experiences</h2>
            <p>Today's events are designed to engage all the senses. We're seeing increased demand for multi-sensory elements—custom fragrances, curated soundscapes, and interactive installations that invite guests to participate rather than simply observe.</p>
            
            <h2>Intimate Grandeur</h2>
            <p>While the scale of gatherings has shifted, the desire for grandeur remains. We're creating intimate events with maximum impact, focusing on quality over quantity and ensuring every detail receives meticulous attention.</p>
            
            <h2>Personalization at Scale</h2>
            <p>Technology enables unprecedented personalization. From AI-assisted seating arrangements to custom-printed menus that reflect each guest's preferences, the luxury events of 2024 feel personally crafted for every attendee.</p>
          `,
          image_url: 'https://images.unsplash.com/photo-1633443682042-17462ad4ad76?q=85&w=1200&auto=format&fit=crop',
          category: 'Design Trends',
          author_name: 'Sarah Mitchell',
          created_at: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-24 bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#D4AF37]">Return to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 bg-[#050505]" data-testid="blog-post-page">
      <SEO
        title={`${post.title} | White Glove Event Concierge`}
        description={post.excerpt || post.title}
        image={post.image_url}
        jsonLd={articleLd({
          title: post.title,
          description: post.excerpt || post.title,
          image: post.image_url,
          datePublished: post.created_at,
          author: post.author_name,
        })}
      />
      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </Link>
      </div>

      {/* Hero Image */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full h-[50vh] relative"
      >
        <img
          src={post.image_url}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </motion.div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 -mt-32 relative z-10 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
            {post.category}
          </span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-6">
            {post.title}
          </h1>
          
          <div className="flex items-center justify-between border-b border-white/10 pb-6 mb-8">
            <div className="flex items-center gap-6 text-white/60 text-sm">
              <span className="flex items-center gap-2">
                <User size={14} />
                {post.author_name}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={14} />
                {format(new Date(post.created_at), 'MMMM d, yyyy')}
              </span>
            </div>
            <button className="text-white/60 hover:text-[#D4AF37] transition-colors" aria-label="Share">
              <Share2 size={18} />
            </button>
          </div>

          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-white
              prose-p:text-white/70 prose-p:leading-relaxed
              prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
            "
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(post.content, {
                ALLOWED_TAGS: [
                  'p', 'br', 'strong', 'em', 'u', 'i', 'b',
                  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                  'ul', 'ol', 'li', 'blockquote',
                  'a', 'img', 'figure', 'figcaption',
                  'hr', 'span', 'div', 'pre', 'code',
                ],
                ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class'],
                ALLOW_DATA_ATTR: false,
              }),
            }}
          />
        </motion.div>
      </article>
    </div>
  );
};
